import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import type { DriverDrawerStep, DriverDrawerType } from '../types';

export interface FooterButtonsProps {
  canSubmit?: boolean;
  canAddImages?: boolean;
  type: DriverDrawerType;
  step: DriverDrawerStep;
  isLoading?: boolean;
  onFormDoneClick: () => void;
  onImagesDoneClick: () => void;
  onCancelClickButton: () => void;
  onDeleteClickButton: () => void;
  goToStep: (step: DriverDrawerStep) => void;
}

export const FooterButtons = ({
  canSubmit,
  canAddImages,
  step = 'form',
  type = 'add',
  isLoading = false,
  onFormDoneClick,
  onImagesDoneClick,
  onCancelClickButton,
  onDeleteClickButton,
  goToStep,
}: FooterButtonsProps) => {
  const { t } = useTranslation('ui-business');
  const { isAccessAllowed } = usePermissions();
  const canEditDriver = isAccessAllowed('create-update-delete:driver');

  const formLabel = type === 'add' && canAddImages ? t('drawers.driver.next') : t('drawers.driver.save');
  const imagesLabel = type === 'add' ? t('drawers.driver.save') : t('drawers.driver.confirm');
  const buttonLabel = step === 'form' ? formLabel : imagesLabel;
  const buttonAction = step === 'form' ? onFormDoneClick : onImagesDoneClick;
  const notAllowed = !canEditDriver && step === 'form';
  const submitDisabled = !canSubmit || notAllowed;

  const showDelete = type === 'edit' && step === 'form';
  const showBack = type === 'add' && canAddImages;

  return (
    <div className='flex w-full items-center gap-2'>
      {showDelete && (
        <Tooltip enable={!canEditDriver} content={t('drawers.driver.not_allowed_to_delete')}>
          <Button variant='ghost' disabled={!canEditDriver} color='danger' startIcon={<Icon name='trash' />} onClick={canEditDriver ? onDeleteClickButton : undefined}>
            {t('drawers.driver.delete_driver')}
          </Button>
        </Tooltip>
      )}
      {showBack && (
        <Button variant='ghost' disabled={step === 'form'} onClick={() => goToStep('form')}>
          {t('drawers.driver.back')}
        </Button>
      )}
      <div className='flex-1' />
      <Button variant='ghost' onClick={onCancelClickButton}>
        {t('drawers.driver.cancel')}
      </Button>
      <Tooltip enable={notAllowed} content={t('drawers.driver.not_allowed_to_add_or_edit')}>
        <Button onClick={buttonAction} disabled={submitDisabled} loading={isLoading}>
          {buttonLabel}
        </Button>
      </Tooltip>
    </div>
  );
};

export default FooterButtons;
